import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { designTokens } from '../styles/design-tokens';
import {
  fetchActivity,
  fetchActivitySummary,
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  createPortfolioShareToken,
  fetchUnifiedShares,
  fetchShareSessions,
  revokeShare,
  type ActivityEvent,
  type ActivitySummaryActor,
  type ApiKey,
  type CreatePortfolioShareTokenResponse,
  type UnifiedShare,
  type ShareSession,
  type ShareType,
} from '../lib/admin-api';

type Tab = 'activity' | 'shares' | 'keys';

const CATEGORY_LABELS: Record<string, string> = {
  parcel_view: 'Viewed parcel',
  briefing: 'Opened briefing',
  share: 'Created share link',
  county_drill: 'Explored county',
  assembly_view: 'Viewed assembly',
  search: 'Searched parcels',
  map_browse: 'Map browsing',
  api_call: 'API call',
};

interface Session {
  start: string;
  end: string;
  events: ActivityEvent[];
}

function groupIntoSessions(events: ActivityEvent[]): Session[] {
  if (!events.length) return [];
  const sorted = [...events].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const sessions: Session[] = [];
  let current: ActivityEvent[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const gap = new Date(sorted[i].created_at).getTime() - new Date(sorted[i - 1].created_at).getTime();
    if (gap > 15 * 60 * 1000 || sorted[i].actor !== current[0].actor) {
      sessions.push({ start: current[0].created_at, end: current[current.length - 1].created_at, events: current });
      current = [sorted[i]];
    } else {
      current.push(sorted[i]);
    }
  }
  sessions.push({ start: current[0].created_at, end: current[current.length - 1].created_at, events: current });
  return sessions.reverse(); // newest first
}

interface CollapsedEvent {
  type: 'single';
  event: ActivityEvent;
}
interface CollapsedMapBrowse {
  type: 'map_browse';
  count: number;
  durationMin: number;
}
type DisplayItem = CollapsedEvent | CollapsedMapBrowse;

function collapseSessionEvents(events: ActivityEvent[]): DisplayItem[] {
  const items: DisplayItem[] = [];
  let i = 0;
  while (i < events.length) {
    if (events[i].category === 'map_browse') {
      let j = i;
      while (j < events.length && events[j].category === 'map_browse') j++;
      const count = j - i;
      const durationMs = new Date(events[j - 1].created_at).getTime() - new Date(events[i].created_at).getTime();
      items.push({ type: 'map_browse', count, durationMin: Math.round(durationMs / 60000) });
      i = j;
    } else {
      items.push({ type: 'single', event: events[i] });
      i++;
    }
  }
  return items;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatGeo(geo: { city: string | null; region: string | null; country: string | null } | null): string {
  if (!geo) return '';
  const parts = [geo.city, geo.region, geo.country].filter(Boolean);
  return parts.join(', ');
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Activity Tab ──

const ActivityTab: React.FC = () => {
  const { theme } = useTheme();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [summary, setSummary] = useState<ActivitySummaryActor[]>([]);
  const [actorFilter, setActorFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [actRes, sumRes] = await Promise.all([
        fetchActivity({ actor: actorFilter || undefined, limit: 2000 }),
        fetchActivitySummary(),
      ]);
      setEvents(actRes.events);
      setSummary(sumRes.actors);
    } catch (e) {
      console.error('Failed to load activity', e);
    } finally {
      setLoading(false);
    }
  }, [actorFilter]);

  useEffect(() => { load(); }, [load]);

  const sessions = groupIntoSessions(events);
  const actors = summary.map(a => a.actor);

  const toggleSession = (idx: number) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const cardStyle: React.CSSProperties = {
    padding: '10px 14px',
    background: theme.backgroundSecondary,
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${theme.border}`,
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <select
          value={actorFilter}
          onChange={e => setActorFilter(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: designTokens.typography.fontSize.sm,
            fontFamily: designTokens.typography.fontFamily.display,
            background: theme.backgroundSecondary, color: theme.text,
            border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
          }}
        >
          <option value="">All actors</option>
          {actors.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button
          onClick={load}
          style={{
            padding: '6px 12px', fontSize: designTokens.typography.fontSize.sm,
            fontFamily: designTokens.typography.fontFamily.display,
            background: designTokens.colors.brand, color: '#fff', border: 'none',
            borderRadius: designTokens.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Refresh
        </button>
        {loading && <span style={{ fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>Loading...</span>}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        {summary.map(a => (
          <div key={a.actor} style={{ ...cardStyle, minWidth: 180 }}>
            <div style={{ fontWeight: 600, fontSize: designTokens.typography.fontSize.md, marginBottom: 4 }}>{a.actor}</div>
            <div style={{ fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary, lineHeight: 1.6 }}>
              {a.total_requests} requests &middot; {a.parcel_views} parcels &middot; {a.briefings} briefings &middot; {a.shares} shares
              <br />Last seen {formatRelative(a.last_seen)}
            </div>
          </div>
        ))}
      </div>

      {/* Sessions */}
      {sessions.map((session, idx) => {
        const expanded = expandedSessions.has(idx);
        const displayItems = collapseSessionEvents(session.events);
        const durationMin = Math.round((new Date(session.end).getTime() - new Date(session.start).getTime()) / 60000);
        return (
          <div key={idx} style={{ marginBottom: 12, border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.md, overflow: 'hidden' }}>
            <div
              onClick={() => toggleSession(idx)}
              style={{
                padding: '8px 14px', background: theme.backgroundSecondary, cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: designTokens.typography.fontSize.sm, fontWeight: 500,
              }}
            >
              <span>
                <strong>{session.events[0]?.actor}</strong> &mdash; {formatTime(session.start)} &mdash; {session.events.length} events, {durationMin}min
                {(() => {
                  const loc = formatGeo(session.events[0]?.geo || null);
                  return loc ? <span style={{ color: theme.textSecondary, marginLeft: 8 }}>({loc})</span> : null;
                })()}
              </span>
              <span style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.xs }}>{expanded ? '\u25B2' : '\u25BC'}</span>
            </div>
            {/* Collapsed summary always visible */}
            <div style={{ padding: '6px 14px', fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>
              {displayItems.map((item, j) => (
                <div key={j} style={{ padding: '2px 0' }}>
                  {item.type === 'map_browse'
                    ? `Map browsing (${item.count} requests, ${item.durationMin}min)`
                    : `${CATEGORY_LABELS[item.event.category] || item.event.category} — ${item.event.path}`
                  }
                </div>
              ))}
            </div>
            {/* Raw events when expanded */}
            {expanded && (
              <div style={{ padding: '6px 14px', borderTop: `1px solid ${theme.border}`, fontSize: designTokens.typography.fontSize.xs }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: theme.textSecondary, textAlign: 'left' }}>
                      <th style={{ padding: '4px 8px' }}>Time</th>
                      <th style={{ padding: '4px 8px' }}>Category</th>
                      <th style={{ padding: '4px 8px' }}>Method</th>
                      <th style={{ padding: '4px 8px' }}>Path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.events.map(ev => (
                      <tr key={ev.id} style={{ fontFamily: designTokens.typography.fontFamily.mono }}>
                        <td style={{ padding: '2px 8px', whiteSpace: 'nowrap' }}>{new Date(ev.created_at).toLocaleTimeString()}</td>
                        <td style={{ padding: '2px 8px' }}>{ev.category}</td>
                        <td style={{ padding: '2px 8px' }}>{ev.method}</td>
                        <td style={{ padding: '2px 8px', wordBreak: 'break-all' }}>{ev.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {!loading && events.length === 0 && (
        <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.sm }}>No activity found.</div>
      )}
    </div>
  );
};

// ── Keys Tab ──

const KeysTab: React.FC = () => {
  const { theme } = useTheme();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newActor, setNewActor] = useState('');
  const [newRole, setNewRole] = useState<'owner' | 'client'>('client');
  const [newLabel, setNewLabel] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApiKeys();
      setKeys(res.keys);
    } catch (e) {
      console.error('Failed to load keys', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newActor.trim()) return;
    try {
      const res = await createApiKey(newActor.trim(), newRole, newLabel.trim() || undefined, newPassword.trim() || undefined);
      setCreatedKey(res.key);
      setCopied(false);
      setNewActor('');
      setNewLabel('');
      setNewPassword('');
      load();
    } catch (err) {
      console.error('Failed to create key', err);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm('Revoke this key? This cannot be undone.')) return;
    try {
      await revokeApiKey(id);
      load();
    } catch (err) {
      console.error('Failed to revoke key', err);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px', fontSize: designTokens.typography.fontSize.sm,
    fontFamily: designTokens.typography.fontFamily.display,
    background: theme.backgroundSecondary, color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
  };

  return (
    <div>
      {/* Create key form */}
      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          placeholder="Actor name"
          value={newActor}
          onChange={e => setNewActor(e.target.value)}
          style={{ ...inputStyle, width: 160 }}
          required
        />
        <select value={newRole} onChange={e => setNewRole(e.target.value as 'owner' | 'client')} style={inputStyle}>
          <option value="client">client</option>
          <option value="owner">owner</option>
        </select>
        <input
          placeholder="Password (optional)"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          style={{ ...inputStyle, width: 160 }}
        />
        <input
          placeholder="Label (optional)"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          style={{ ...inputStyle, width: 160 }}
        />
        <button
          type="submit"
          style={{
            padding: '6px 14px', fontSize: designTokens.typography.fontSize.sm,
            fontFamily: designTokens.typography.fontFamily.display,
            background: designTokens.colors.brand, color: '#fff', border: 'none',
            borderRadius: designTokens.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Create Key
        </button>
      </form>

      {/* Show newly created key */}
      {createdKey && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: designTokens.borderRadius.md,
          background: designTokens.colors.warningLight, border: `1px solid ${designTokens.colors.warning}`,
        }}>
          <div style={{ fontWeight: 600, fontSize: designTokens.typography.fontSize.sm, marginBottom: 6, color: designTokens.colors.warning }}>
            This key will not be shown again
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{
              flex: 1, padding: '8px 10px', background: theme.background,
              borderRadius: designTokens.borderRadius.sm, fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: designTokens.typography.fontSize.sm, wordBreak: 'break-all', color: theme.text,
            }}>
              {createdKey}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(createdKey); setCopied(true); }}
              style={{
                padding: '6px 12px', fontSize: designTokens.typography.fontSize.sm,
                fontFamily: designTokens.typography.fontFamily.display,
                background: copied ? designTokens.colors.success : theme.backgroundSecondary,
                color: copied ? '#fff' : theme.text,
                border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.sm }}>Loading...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: designTokens.typography.fontSize.sm }}>
          <thead>
            <tr style={{ color: theme.textSecondary, textAlign: 'left', borderBottom: `1px solid ${theme.border}` }}>
              <th style={{ padding: '6px 8px' }}>Actor</th>
              <th style={{ padding: '6px 8px' }}>Role</th>
              <th style={{ padding: '6px 8px' }}>Password</th>
              <th style={{ padding: '6px 8px' }}>Label</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }}>Created</th>
              <th style={{ padding: '6px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{k.actor}</td>
                <td style={{ padding: '6px 8px' }}>{k.role}</td>
                <td style={{ padding: '6px 8px', fontFamily: designTokens.typography.fontFamily.mono }}>{k.password_hint || '—'}</td>
                <td style={{ padding: '6px 8px', color: theme.textSecondary }}>{k.label || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: designTokens.borderRadius.full,
                    fontSize: designTokens.typography.fontSize.xs, fontWeight: 600,
                    background: k.revoked_at ? designTokens.colors.errorLight : designTokens.colors.successLight,
                    color: k.revoked_at ? designTokens.colors.error : designTokens.colors.success,
                  }}>
                    {k.revoked_at ? 'revoked' : 'active'}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', color: theme.textSecondary }}>{new Date(k.created_at).toLocaleDateString()}</td>
                <td style={{ padding: '6px 8px' }}>
                  {!k.revoked_at && (
                    <button
                      onClick={() => handleRevoke(k.id)}
                      style={{
                        padding: '3px 10px', fontSize: designTokens.typography.fontSize.xs,
                        fontFamily: designTokens.typography.fontFamily.display,
                        background: 'none', color: designTokens.colors.error,
                        border: `1px solid ${designTokens.colors.error}`, borderRadius: designTokens.borderRadius.sm,
                        cursor: 'pointer',
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Unified Shares Tab ─────────────────────────────────────────────────────
//
// One table for all three share types: parcel-briefing, zone, portfolio.
// Per-row stats come from the activity_log JOIN done server-side: hit count,
// unique IPs, last opened. Expand a row to see session-grouped opens (same IP
// within 30min = one session, bots flagged but not hidden).
//
// Keeps the portfolio create form at the top — only entry point to mint a
// portfolio share. Briefing + zone shares are created from their respective
// detail pages and surface here read-only.

function shareUrl(type: ShareType, target_id: string, token: string, carveOutSlug?: string | null, profile?: string | null, layouts?: string | null): string {
  const prefix = type === 'briefing' ? 'briefing' : type === 'zone' ? 'zones' : 'portfolio';
  // carve_out_slug, profile and layouts together reconstruct the exact link sent to
  // the client — omitting any of them shows a different view than the one received.
  return `${window.location.origin}/${prefix}/${target_id}?token=${token}${carveOutSlug ? `&carveOut=${encodeURIComponent(carveOutSlug)}` : ''}${profile === 'grid' ? '&profile=grid' : ''}${layouts === 'alt' ? '&layouts=alt' : ''}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

const TYPE_LABEL: Record<ShareType, string> = {
  briefing: 'Briefing',
  zone: 'Zone',
  portfolio: 'Portfolio',
};

const TYPE_COLOR: Record<ShareType, string> = {
  briefing: '#5a8db8',
  zone: '#c4705a',
  portfolio: '#7a9b6e',
};

const SharesTab: React.FC = () => {
  const { theme } = useTheme();
  const [shares, setShares] = useState<UnifiedShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<ShareType | 'all'>('all');
  const [showRevoked, setShowRevoked] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sessionsCache, setSessionsCache] = useState<Record<string, ShareSession[]>>({});
  const [sessionsLoading, setSessionsLoading] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Portfolio create form state — only way to mint a portfolio share.
  const [pfClientSlug, setPfClientSlug] = useState('integra');
  const [pfLabel, setPfLabel] = useState('');
  const [pfExpiresInDays, setPfExpiresInDays] = useState(180);
  const [pfCreating, setPfCreating] = useState(false);
  const [pfCreated, setPfCreated] = useState<CreatePortfolioShareTokenResponse | null>(null);
  const [pfError, setPfError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchUnifiedShares();
      setShares(res.shares);
    } catch (e) {
      console.error('Failed to load unified shares', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = Date.now();

  const handleRevoke = async (type: ShareType, id: string) => {
    if (!confirm(`Revoke this ${TYPE_LABEL[type].toLowerCase()} share link? It will stop working immediately.`)) return;
    try {
      await revokeShare(type, id);
      load();
    } catch (err) {
      console.error('Failed to revoke share', err);
    }
  };

  const handleExpand = async (rowKey: string, type: ShareType, id: string) => {
    if (expanded === rowKey) {
      setExpanded(null);
      return;
    }
    setExpanded(rowKey);
    if (!sessionsCache[rowKey]) {
      setSessionsLoading(rowKey);
      try {
        const res = await fetchShareSessions(type, id);
        setSessionsCache((prev) => ({ ...prev, [rowKey]: res.sessions }));
      } catch (err) {
        console.error('Failed to load sessions', err);
        setSessionsCache((prev) => ({ ...prev, [rowKey]: [] }));
      } finally {
        setSessionsLoading(null);
      }
    }
  };

  const handleCopyUrl = (rowKey: string, url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(rowKey);
    setTimeout(() => setCopied((cur) => (cur === rowKey ? null : cur)), 1500);
  };

  const handlePortfolioCreate = async () => {
    setPfCreating(true);
    setPfError(null);
    setPfCreated(null);
    try {
      const res = await createPortfolioShareToken(
        pfClientSlug.trim(),
        pfLabel.trim() || undefined,
        pfExpiresInDays,
      );
      setPfCreated(res);
      setPfLabel('');
      load();
    } catch (err: unknown) {
      setPfError(err instanceof Error ? err.message : 'Failed to create portfolio share');
    } finally {
      setPfCreating(false);
    }
  };

  const filtered = shares.filter((s) => {
    if (typeFilter !== 'all' && s.type !== typeFilter) return false;
    if (!showRevoked && (s.revoked_at || new Date(s.expires_at).getTime() < now)) return false;
    return true;
  });

  const inputStyle: React.CSSProperties = {
    padding: '6px 8px', fontSize: designTokens.typography.fontSize.sm,
    border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
    background: theme.background, color: theme.text,
  };

  const pillBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', fontSize: designTokens.typography.fontSize.xs, fontWeight: 600,
    fontFamily: designTokens.typography.fontFamily.display,
    background: active ? designTokens.colors.brand : 'transparent',
    color: active ? '#fff' : theme.textSecondary,
    border: `1px solid ${active ? designTokens.colors.brand : theme.border}`,
    borderRadius: designTokens.borderRadius.full, cursor: 'pointer',
  });

  return (
    <div>
      {/* Portfolio create form — only entry point to mint a portfolio share. */}
      <div style={{
        padding: 16, marginBottom: 20,
        border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.md,
        background: theme.surface,
      }}>
        <div style={{ fontSize: designTokens.typography.fontSize.sm, fontWeight: 600, marginBottom: 10 }}>
          Create portfolio share link
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 100px auto', gap: 8, alignItems: 'end' }}>
          <label style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Client slug
            <input
              value={pfClientSlug}
              onChange={(e) => setPfClientSlug(e.target.value)}
              placeholder="integra"
              style={{ ...inputStyle, fontFamily: designTokens.typography.fontFamily.mono }}
            />
          </label>
          <label style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Label (recipient name — shown in activity log)
            <input
              value={pfLabel}
              onChange={(e) => setPfLabel(e.target.value)}
              placeholder="e.g. Integra — head of land"
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Expires (days)
            <input
              type="number" min={1} max={365}
              value={pfExpiresInDays}
              onChange={(e) => setPfExpiresInDays(Math.max(1, Math.min(365, Number(e.target.value) || 180)))}
              style={{ ...inputStyle, fontFamily: designTokens.typography.fontFamily.mono }}
            />
          </label>
          <button
            onClick={handlePortfolioCreate}
            disabled={pfCreating || !pfClientSlug.trim()}
            style={{
              padding: '7px 16px', fontSize: designTokens.typography.fontSize.sm, fontWeight: 600,
              fontFamily: designTokens.typography.fontFamily.display,
              background: designTokens.colors.brand, color: '#fff',
              border: 'none', borderRadius: designTokens.borderRadius.sm,
              cursor: pfCreating ? 'not-allowed' : 'pointer', opacity: pfCreating ? 0.5 : 1,
            }}
          >
            {pfCreating ? 'Creating…' : 'Create link'}
          </button>
        </div>
        {pfError && (
          <div style={{ marginTop: 10, fontSize: designTokens.typography.fontSize.sm, color: designTokens.colors.error }}>
            {pfError}
          </div>
        )}
        {pfCreated && (
          <div style={{
            marginTop: 12, padding: '8px 10px',
            background: theme.background, border: `1px solid ${theme.border}`,
            borderRadius: designTokens.borderRadius.sm,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <code style={{
              flex: 1, fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: designTokens.typography.fontSize.xs, color: theme.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {pfCreated.url}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(pfCreated.url).catch(() => {})}
              style={{
                padding: '4px 10px', fontSize: designTokens.typography.fontSize.xs,
                background: 'transparent', border: `1px solid ${theme.border}`,
                borderRadius: designTokens.borderRadius.sm, color: theme.text, cursor: 'pointer',
              }}
            >
              Copy
            </button>
          </div>
        )}
      </div>

      {/* Filter controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => setTypeFilter('all')} style={pillBtnStyle(typeFilter === 'all')}>All</button>
        <button onClick={() => setTypeFilter('briefing')} style={pillBtnStyle(typeFilter === 'briefing')}>Briefings</button>
        <button onClick={() => setTypeFilter('zone')} style={pillBtnStyle(typeFilter === 'zone')}>Zones</button>
        <button onClick={() => setTypeFilter('portfolio')} style={pillBtnStyle(typeFilter === 'portfolio')}>Portfolios</button>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, cursor: 'pointer' }}>
          <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
          Show revoked / expired
        </label>
        <button
          onClick={load}
          style={{
            padding: '6px 12px', fontSize: designTokens.typography.fontSize.sm,
            fontFamily: designTokens.typography.fontFamily.display,
            background: designTokens.colors.brand, color: '#fff', border: 'none',
            borderRadius: designTokens.borderRadius.sm, cursor: 'pointer', marginLeft: 'auto',
          }}
        >
          Refresh
        </button>
        {loading && <span style={{ fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>Loading…</span>}
      </div>

      {!loading && filtered.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.sm, padding: '20px 0' }}>
          No share links match the current filter.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: designTokens.typography.fontSize.sm }}>
          <thead>
            <tr style={{ color: theme.textSecondary, textAlign: 'left', borderBottom: `1px solid ${theme.border}` }}>
              <th style={{ padding: '6px 8px' }}>Type</th>
              <th style={{ padding: '6px 8px' }}>Label · Target</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sessions</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>IPs</th>
              <th style={{ padding: '6px 8px' }}>Locations</th>
              <th style={{ padding: '6px 8px' }}>Last opened</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }}>Expires</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const rowKey = `${s.type}:${s.id}`;
              const revoked = !!s.revoked_at;
              const expired = new Date(s.expires_at).getTime() < now;
              const inactive = revoked || expired;
              const url = shareUrl(s.type, s.target_id, s.token, s.carve_out_slug, s.profile, s.layouts);
              const sessions = sessionsCache[rowKey];
              const isExpanded = expanded === rowKey;
              return (
                <React.Fragment key={rowKey}>
                  <tr style={{ borderBottom: `1px solid ${theme.border}`, opacity: inactive ? 0.55 : 1 }}>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: designTokens.borderRadius.full,
                        fontSize: designTokens.typography.fontSize.xs, fontWeight: 600,
                        background: TYPE_COLOR[s.type] + '22', color: TYPE_COLOR[s.type],
                      }}>
                        {TYPE_LABEL[s.type]}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ fontWeight: 500 }}>
                        {s.label || <span style={{ color: theme.textSecondary, fontStyle: 'italic' }}>unlabeled</span>}
                      </div>
                      <div style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary }}>
                        {s.target_label}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontFamily: designTokens.typography.fontFamily.mono }}>
                      {s.hit_count}
                      <div style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, fontWeight: 400 }}>
                        {s.view_count} pg loads
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.mono }}>
                      {s.unique_ips}
                      {s.unique_ips > s.human_ips && (
                        <div style={{ fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary, fontWeight: 400 }}>
                          {s.human_ips} human
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: designTokens.typography.fontSize.xs, color: theme.textSecondary }}>
                      {(() => {
                        const tc = s.top_cities || [];
                        if (tc.length === 0) return '—';
                        const shown = tc.slice(0, 2).map((c) => c.city).filter(Boolean).join(', ');
                        const extra = s.human_city_count - 2;
                        const title = tc.map((c) => [c.city, c.region].filter(Boolean).join(', ')).join(' · ')
                          + (s.human_city_count > tc.length ? ` +${s.human_city_count - tc.length} more` : '');
                        return <span title={title}>{shown}{extra > 0 ? ` +${extra}` : ''}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', color: theme.textSecondary, fontSize: designTokens.typography.fontSize.xs }}>
                      {s.last_opened_at ? formatRelative(s.last_opened_at) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: designTokens.borderRadius.full,
                        fontSize: designTokens.typography.fontSize.xs, fontWeight: 600,
                        background: inactive ? designTokens.colors.errorLight : designTokens.colors.successLight,
                        color: inactive ? designTokens.colors.error : designTokens.colors.success,
                      }}>
                        {revoked ? 'revoked' : expired ? 'expired' : 'active'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', color: theme.textSecondary, fontSize: designTokens.typography.fontSize.xs }}>
                      {new Date(s.expires_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleCopyUrl(rowKey, url)}
                        style={{
                          padding: '3px 8px', fontSize: designTokens.typography.fontSize.xs,
                          background: copied === rowKey ? designTokens.colors.success : 'transparent',
                          color: copied === rowKey ? '#fff' : theme.text,
                          border: `1px solid ${copied === rowKey ? designTokens.colors.success : theme.border}`,
                          borderRadius: designTokens.borderRadius.sm, cursor: 'pointer', marginRight: 4,
                        }}
                      >
                        {copied === rowKey ? 'Copied' : 'Copy URL'}
                      </button>
                      <button
                        onClick={() => handleExpand(rowKey, s.type, s.id)}
                        disabled={s.hit_count === 0}
                        style={{
                          padding: '3px 8px', fontSize: designTokens.typography.fontSize.xs,
                          background: 'transparent', border: `1px solid ${theme.border}`,
                          borderRadius: designTokens.borderRadius.sm, color: theme.text,
                          cursor: s.hit_count === 0 ? 'not-allowed' : 'pointer',
                          opacity: s.hit_count === 0 ? 0.4 : 1, marginRight: 4,
                        }}
                      >
                        {isExpanded ? 'Hide' : 'Sessions'}
                      </button>
                      {!inactive && (
                        <button
                          onClick={() => handleRevoke(s.type, s.id)}
                          style={{
                            padding: '3px 8px', fontSize: designTokens.typography.fontSize.xs,
                            background: 'transparent', border: `1px solid ${designTokens.colors.error}`,
                            borderRadius: designTokens.borderRadius.sm, color: designTokens.colors.error,
                            cursor: 'pointer',
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={9} style={{ padding: '0 16px 12px', background: theme.background }}>
                        {sessionsLoading === rowKey ? (
                          <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.xs, padding: 8 }}>
                            Loading sessions…
                          </div>
                        ) : sessions && sessions.length === 0 ? (
                          <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.xs, padding: 8 }}>
                            No sessions recorded yet. (If the recipient just opened the link, refresh in a few seconds — there's a small write lag.)
                          </div>
                        ) : sessions ? (
                          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                            <table style={{ width: '100%', fontSize: designTokens.typography.fontSize.xs, borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ color: theme.textSecondary }}>
                                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>When</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>IP</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Location</th>
                                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Duration</th>
                                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Hits</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>User-Agent</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sessions.map((sess, j) => (
                                  <tr key={j} style={{ borderTop: `1px solid ${theme.border}`, opacity: sess.is_bot ? 0.5 : 1 }}>
                                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                                      {new Date(sess.started_at).toLocaleString()}
                                      <div style={{ color: theme.textSecondary, fontSize: 10 }}>{formatRelative(sess.started_at)}</div>
                                    </td>
                                    <td style={{ padding: '4px 8px', fontFamily: designTokens.typography.fontFamily.mono }}>
                                      {sess.ip || '—'}
                                      {sess.is_bot && (
                                        <span style={{
                                          marginLeft: 6, padding: '1px 6px', borderRadius: designTokens.borderRadius.full,
                                          fontSize: 9, fontWeight: 600,
                                          background: theme.textSecondary + '22', color: theme.textSecondary,
                                        }}>
                                          bot
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '4px 8px', color: theme.textSecondary }}>
                                      {formatGeo(sess.geo) || '—'}
                                    </td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.mono }}>
                                      {formatDuration(sess.duration_seconds)}
                                    </td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.mono }}>
                                      {sess.hit_count}
                                    </td>
                                    <td style={{ padding: '4px 8px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sess.user_agent || ''}>
                                      {sess.user_agent || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Admin Page ──

const AdminPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { theme } = useTheme();
  const [tab, setTab] = useState<Tab>('activity');

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 16px', fontSize: designTokens.typography.fontSize.sm, fontWeight: 500,
    fontFamily: designTokens.typography.fontFamily.display,
    cursor: 'pointer', border: 'none', borderRadius: designTokens.borderRadius.sm,
    background: active ? designTokens.colors.brand : 'transparent',
    color: active ? '#fff' : theme.textSecondary,
  });

  return (
    <div style={{
      minHeight: '100vh', background: theme.background, color: theme.text,
      fontFamily: designTokens.typography.fontFamily.display,
    }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              padding: '4px 10px', fontSize: designTokens.typography.fontSize.sm,
              fontFamily: designTokens.typography.fontFamily.display,
              background: 'none', color: theme.textSecondary,
              border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
              cursor: 'pointer',
            }}
          >
            &larr; Back
          </button>
          <h1 style={{ fontSize: designTokens.typography.fontSize.xl, fontWeight: 600, margin: 0 }}>Admin</h1>
          {/* Quick links — owner views that have no menu path (carveOut URLs are hand-built) */}
          <a
            href="/briefing/912d47ae-b055-4716-9375-7946623b7f3b?carveOut=crownpine-blk-194-a1"
            style={{
              marginLeft: 'auto', padding: '4px 10px', fontSize: designTokens.typography.fontSize.sm,
              fontFamily: designTokens.typography.fontFamily.display,
              color: theme.textSecondary, textDecoration: 'none',
              border: `1px solid ${theme.border}`, borderRadius: designTokens.borderRadius.sm,
            }}
          >
            Crown Pine A1 &rarr;
          </a>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          <button onClick={() => setTab('activity')} style={tabBtnStyle(tab === 'activity')}>Activity</button>
          <button onClick={() => setTab('shares')} style={tabBtnStyle(tab === 'shares')}>Shares</button>
          <button onClick={() => setTab('keys')} style={tabBtnStyle(tab === 'keys')}>Keys</button>
        </div>

        {/* Content */}
        {tab === 'activity' && <ActivityTab />}
        {tab === 'shares' && <SharesTab />}
        {tab === 'keys' && <KeysTab />}
      </div>
    </div>
  );
};

export default AdminPage;
